import {
  ApplicantStatus,
  ExecutionModes,
  VerificationStatus,
  VerificationNames,
  ApplicantSettings
} from "shared/types";
import { Applicant, Application } from "types/application";
import { CreditReport, VerificationReport } from "types/landlord/reports";
import { DefinedQueryObserverResult } from "@tanstack/react-query";
import CreditReportSlice from "..";
import { ManualReportView } from "views/applications-id-report/components/ReportsFallback/ManualReportView";
import { SkippedReportView } from "views/applications-id-report/components/ReportsFallback/SkippedReportView";
import { ProgressReportView } from "views/applications-id-report/components/ReportsFallback/ProgressReportView";
import { PendingReportView } from "views/applications-id-report/components/ReportsFallback/PendingReportView";
import { UnavailableReportView } from "views/applications-id-report/components/ReportsFallback/UnavailableReportView";
import { HugeiconsIcon } from "@hugeicons/react";
import { Forward02Icon } from "@hugeicons-pro/core-stroke-rounded";

export const CreditReportByStatus = ({
  verificationCreditReport,
  isFetched,
  applicant,
  applicationMeta,
  screeningCriteria,
  selectedVerificationId,
  activeReportId,
  openRerunModal,
  setOpenRerunModal,
  isIdentityVerificationOverrideRequired,
  isSIN
}: {
  verificationCreditReport?: VerificationReport<CreditReport>;
  isFetched?: boolean;
  applicant: Applicant;
  applicationMeta: DefinedQueryObserverResult<Application | undefined, unknown>;
  screeningCriteria?: ApplicantSettings;
  selectedVerificationId: number;
  activeReportId?: number;
  openRerunModal: boolean;
  setOpenRerunModal: (val: boolean) => void;
  isIdentityVerificationOverrideRequired: boolean;
  isSIN?: boolean;
}) => {
  if (verificationCreditReport?.skips_for_canadian_resident)
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-6">
        <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center">
          <HugeiconsIcon
            icon={Forward02Icon}
            size={24}
            className="text-primary-500"
          />
        </div>
        <div className="text-center max-w-[432px] space-y-2">
          <p className="text-content-1000 text-base font-bold">
            Credit report will be skipped
          </p>
          <p className="text-content-300 text-sm font-medium">
            Applicant has a Canadian address. The US credit report will be
            skipped — use the Canadian credit report instead.
          </p>
        </div>
      </div>
    );

  if (
    verificationCreditReport?.status === VerificationStatus.Pending &&
    verificationCreditReport.settings?.execution_mode === ExecutionModes.Manual
  )
    return (
      <ManualReportView
        refetch={applicationMeta.refetch}
        applicantStatus={
          applicationMeta.data!.applicants.find(
            (appl) => appl.id === applicant.id
          )?.application_status
        }
        applicationStatus={applicationMeta.data!.status}
        applicantId={applicant.id}
        verificationId={verificationCreditReport.id}
        applicationId={applicationMeta.data!.id}
        verificationName={VerificationNames.CreditReport}
        creditReportStatus={verificationCreditReport.status}
      />
    );

  if (
    (verificationCreditReport?.status === VerificationStatus.Pending ||
      verificationCreditReport?.status === VerificationStatus.Skipped) &&
    verificationCreditReport.settings?.execution_mode ===
      ExecutionModes.Conditionally
  )
    return (
      <SkippedReportView
        refetch={applicationMeta.refetch}
        applicantId={applicant.id}
        verificationId={verificationCreditReport.id}
        applicationId={applicationMeta.data!.id}
        verificationStatus={verificationCreditReport.status}
      />
    );

  if (
    verificationCreditReport?.status === VerificationStatus.Pending &&
    (verificationCreditReport.is_rerun ||
      applicant.application_status === ApplicantStatus.Submitted)
  )
    return (
      <ProgressReportView
        message={
          verificationCreditReport.is_rerun
            ? "Report re-run in progress. Please wait"
            : undefined
        }
      />
    );

  if (isIdentityVerificationOverrideRequired)
    return (
      <PendingReportView message="Please review issues with applicant's identity verification report. This report will not run until identity is verified" />
    );

  if (verificationCreditReport?.status === VerificationStatus.Pending)
    return <PendingReportView />;

  if (verificationCreditReport?.status === VerificationStatus.Canceled)
    return <PendingReportView message="Report was canceled" />;

  if (verificationCreditReport?.status === VerificationStatus.Started)
    return <ProgressReportView />;

  if (verificationCreditReport?.status === VerificationStatus.NoDataAvailable)
    return <UnavailableReportView />;

  return (
    <CreditReportSlice
      creditReport={verificationCreditReport}
      isFetched={isFetched}
      applicant={applicant}
      screeningCriteria={screeningCriteria}
      applicationStatus={applicationMeta.data!.status}
      verificationId={selectedVerificationId}
      activeReportId={activeReportId}
      openRerunModal={openRerunModal}
      setOpenRerunModal={setOpenRerunModal}
      isSIN={isSIN}
      pastDueAccountType={
        applicationMeta.data!.verification_template?.screening_criteria
          ? applicationMeta.data!.verification_template.screening_criteria[
              `${applicant.role}_settings`
            ]?.past_due_account?.past_due_account_rate
          : ""
      }
    />
  );
};
